<template>
	<Drawer
		:initialWidth="initialWidth"
		:closeOnClickOutside="true"
		:skipOpenAnimation="skipOpenAnimation"
		@requestUnmount="hide"
	>
		<template #header>
			<div class="flex items-start justify-between w-full">
				<div class="font-semibold text-md">
					{{ $t('Lesson plan') }}
				</div>
				<button type="button" class="btn btn-sm btn-circle btn-ghost bg-base-100 mr-2" @click="printLessonplan()" :title="$t('Print report')">
					<PrinterIcon class="w-4 h-4" />
				</button>
			</div>
		</template>
		<template #default="{ hide }">
			<div style="display: none">{{ setSlotHide(hide) }}</div>

			<div class="h-full px-6 pb-24 overflow-y-auto relative">

				<!-- header -->
				<div class="sticky top-0 z-20 bg-base/80 backdrop-blur-md pb-2 pt-4 mb-6">

					<slot name="topMessage"></slot>

					<div class="flex items-center">
						<div class="flex-1 min-w-0">
							<h1 class="w-full text-xl font-semibold">
								{{ lessonPlan.title }}
							</h1>
							<div v-if="showCreator" class="flex gap-2">
								<div class="flex items-center gap-2 text-xs text-accent-focus bg-accent-focus/10 py-0.5 px-2 mt-0.5 max-w-max rounded">
									<!-- Phosphor icon PenNib -->
									<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="currentColor" viewBox="0 0 256 256">
										<path d="M248,92.68a15.86,15.86,0,0,0-4.69-11.31L174.63,12.68a16,16,0,0,0-22.63,0L123.57,41.11l-58,21.77A16.06,16.06,0,0,0,55.35,75.23L32.11,214.68A8,8,0,0,0,40,224a8.4,8.4,0,0,0,1.32-.11l139.44-23.24a16,16,0,0,0,12.35-10.17l21.77-58L243.31,104A15.87,15.87,0,0,0,248,92.68Zm-69.87,92.19L63.32,204l47.37-47.37a28,28,0,1,0-11.32-11.32L52,192.7,71.13,77.86,126,57.29,198.7,130ZM112,132a12,12,0,1,1,12,12A12,12,0,0,1,112,132Zm96-15.32L139.31,48l24-24L232,92.68Z"></path>
									</svg>
									<p>{{ lessonPlan.creatorName }}</p>
									<div v-if="lessonPlan.coCreatorName" class="flex items-center gap-2">
										<span>/</span><span>{{ lessonPlan.coCreatorName }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="flex flex-col gap-y-8 font-medium rounded">

					<!-- Supplies & Attachments -->
					<div class="grid grid-cols-2 gap-4">
						<div class="border-2 border-base-100 rounded-lg p-4">
							<div class="font-semibold mb-2 text-xs uppercase text-base-content-light">
								{{ $t("Supplies") }}
							</div>
							<ul v-if="lessonPlan?.supplies?.length > 0" class="list-disc pl-5 space-y-1">
								<li
									v-for="(item, idx) in showAllSupplies
										? lessonPlan.supplies
										: lessonPlan.supplies.slice(0, 3)"
									:key="item && item.id ? item.id : item + '_' + idx"
								>
									{{ item }}
								</li>
							</ul>
							<div v-if="lessonPlan?.supplies?.length > 3" class="mt-1">
								<button
									type="button"
									class="btn btn-sm btn-ghost"
									@click="showAllSupplies = !showAllSupplies"
								>
									<span v-if="!showAllSupplies" class="flex items-center gap-1 text-accent-focus lowercase">
										+<i18n-t keypath="{counter} more" tag="span">
											<template #counter>
												<span>{{ lessonPlan.supplies.length - 3 }}</span>
											</template>
										</i18n-t>
										<ChevronDownIcon class="w-4 h-4" />
									</span>
									<span v-else class="flex items-center gap-2">
										<span class="text-accent-focus lowercase">{{ $t("Show less") }}</span>
										<ChevronUpIcon class="w-4 h-4 text-accent-focus" />
									</span>
								</button>
							</div>
							<div v-else-if="!lessonPlan?.supplies?.length" class="text-sm italic text-base-content-light">
								{{ $t("None") }}
							</div>
						</div>
						<div class="border-2 border-base-100 rounded-lg p-4">
							<div class="font-semibold mb-2 text-xs uppercase text-base-content-light">
								{{ $t("Attachments") }}
							</div>
							<Attachments
								v-if="lessonPlan?.attachments?.length > 0"
								:key="showAllAttachments ? 'all' : 'limited'"
								:modelValue="visibleAttachments"
								:preview="false"
								:header="false"
								:externalFilesButton="false"
								:readOnly="true"
							/>
							<div v-if="lessonPlan?.attachments?.length > 3" class="mt-1">
								<button
									type="button"
									class="btn btn-sm btn-ghost"
									@click="showAllAttachments = !showAllAttachments"
								>
									<span v-if="!showAllAttachments" class="flex items-center gap-1 text-accent-focus lowercase">
										+<i18n-t keypath="{counter} more" tag="span">
											<template #counter>
												<span>{{ lessonPlan.attachments.length - 3 }}</span>
											</template>
										</i18n-t>
										<ChevronDownIcon class="w-4 h-4" />
									</span>
									<span v-else class="flex items-center gap-2">
										<span class="text-accent-focus lowercase">{{ $t("Show less") }}</span>
										<ChevronUpIcon class="w-4 h-4 text-accent-focus" />
									</span>
								</button>
							</div>
							<div v-else-if="!lessonPlan?.attachments?.length" class="text-sm italic text-base-content-light">
								{{ $t("None") }}
							</div>
						</div>
					</div>

					<!-- Description -->
					<TextEditor
						v-if="lessonPlan"
						:modelValue="lessonPlan.instructions"
						:readOnly="true"
					/>

					<!-- Goals -->
					<div class="px-4">
						<div class="font-semibold mb-4 text-xs uppercase text-base-content-light">
							{{ $tc("Goal", 2) }}
						</div>
						<GoalsDisplay
							v-if="lessonPlan.goals?.length"
							:goals="lessonPlan.goals"
							:curriculum="organisationsStore.currentOrganisation?.curriculum ?? null"
						/>
						<div v-else class="text-sm italic text-base-content-light">
							{{ $t("None") }}
						</div>
					</div>

					<!-- Gaan we nog nagaan of het nodig is of niet -->
					<!-- <div v-if="showOptionGuided" class="flex items-center gap-2">
							<input
								type="checkbox"
								class="checkbox checkbox-sm"
								v-model.lazy="lessonPlan.isGuided"
								name="guided"
								id="guided-activity"
								:disabled="readOnly"
							/>
							<label for="guided-activity" class="flex gap-2">
								<span class="text-sm mt-0.5">{{ $t("Under guidance of the teacher") }}</span>
								<LifebuoyIcon class="w-6 h-6" />
							</label>
						</div> -->
				</div>
			</div>

			<div v-if="showFooter" class="flex absolute bottom-0 w-full bg-white/80 backdrop-blur-sm z-10 p-4 mt-6 border-t border-base-200 items-center justify-between gap-4">

				<!-- Left: what happens to this lesson plan in my library -->
				<div class="flex items-center gap-2" data-testid="footer-library-action">

					<p v-if="isRemovedFromMyLibrary" class="text-sm text-base-content-light" data-testid="removed-from-library-note">
						{{ $t("This lesson plan has been removed from My library and can no longer be edited.") }}
					</p>

					<!-- An older version of a plan in my library (a past lesson): my library and collections have the
					     latest version, so this button only tells which collections, it changes nothing -->
					<PopoverRoot v-else-if="withLibraryButton && isOutdatedVersionInMyLibrary" @update:open="onOutdatedPopoverOpen">
						<PopoverTrigger as-child>
							<button type="button" class="btn btn-ghost" data-testid="outdated-library-button">
								<span class="relative mr-1">
									<RectangleStackIcon class="w-4 h-4" />
									<div class="w-3 h-3 absolute -top-1 -right-2 bg-orange-500 rounded-full flex justify-center items-center">
										<CheckIcon class="w-2.5 h-2.5 text-base" />
									</div>
								</span>
								{{ $t("In My Library") }}
								<span v-if="lessonPlan.collectionIds?.length" class="badge badge-sm text-white border-0 bg-base-content">{{ lessonPlan.collectionIds.length }}</span>
							</button>
						</PopoverTrigger>
						<PopoverPortal>
							<PopoverContent
								side="top"
								align="center"
								:side-offset="5"
								:collision-padding="16"
								:avoid-collisions="true"
								class="z-50 bg-white rounded-lg shadow-xl border w-96 sm:max-w-md p-4 flex flex-col gap-3 text-sm"
								data-testid="outdated-version-popover"
							>
								<p class="p-2 rounded bg-orange-100 whitespace-pre-line">{{ $t("#info: lesson uses an older lesson plan version") }}</p>
								<div v-if="outdatedVersionCollections.length">
									<p class="font-semibold mb-1">{{ $t("Collection", 2) }}</p>
									<ul class="list-disc list-inside">
										<li v-for="collection in outdatedVersionCollections" :key="collection.id">{{ collection.name }}</li>
									</ul>
								</div>
								<p v-else class="text-base-content-light">{{ $t("Not in any of your collections.") }}</p>
								<PopoverArrow class="fill-white" />
							</PopoverContent>
						</PopoverPortal>
					</PopoverRoot>

					<div v-else-if="withLibraryButton" class="relative">
						<!-- This plan and my library: adding, removing and collections, all in the collections selector -->
						<LessonPlanCollectionsSelector
							:lessonPlan="lessonPlan"
							side="top"
							align="center"
							@collectionsChanged="$emit('collectionsChanged', lessonPlan)"
							@removedFromLibrary="onRemovedFromLibrary"
						>
							<template #trigger="{ toggle, addAndOpen }">
								<button
									type="button"
									class="btn btn-ghost"
									data-testid="library-button"
									@click="lessonPlan.isInMyLibrary ? toggle() : addAndOpen()"
								>
									<template v-if="lessonPlan.isInMyLibrary">
										<span class="relative mr-1">
											<RectangleStackIcon class="w-4 h-4" />
											<div class="w-3 h-3 absolute -top-1 -right-2 bg-green-500 rounded-full flex justify-center items-center">
												<CheckIcon class="w-2.5 h-2.5 text-base" />
											</div>
										</span>
										{{ $t("In My Library") }}
										<span v-if="lessonPlan.collectionIds?.length" class="badge badge-sm text-white border-0 bg-base-content">{{ lessonPlan.collectionIds.length }}</span>
									</template>
									<template v-else>
										<span class="relative mr-1">
											<RectangleStackIcon class="w-4 h-4" />
											<PlusIcon
												class="w-3 h-3 absolute -top-1 -right-2 text-base-content bg-primary rounded-full"
											/>
										</span>
										{{ $t("Add to My library") }}
									</template>
								</button>
							</template>
						</LessonPlanCollectionsSelector>

						<!-- tooltip for the library button, e.g. after creating a lesson plan -->
						<PopoverRoot v-if="collectionTooltipMessage" :defaultOpen="true">
							<!-- covers the library button, so the tooltip lines up with it -->
							<PopoverAnchor class="absolute inset-0 pointer-events-none" />
							<PopoverPortal>
								<PopoverContent
									side="top"
									align="start"
									:side-offset="4"
									:collision-padding="16"
									:avoid-collisions="true"
									class="z-50 text-start text-sm font-semibold text-info-content bg-info w-64 p-3 border border-info-content/20 rounded-md shadow-md"
								>
									<div v-html="collectionTooltipMessage"></div>
									<PopoverArrow rounded class="fill-info stroke-info-content/20 -mt-px" />
								</PopoverContent>
							</PopoverPortal>
						</PopoverRoot>
					</div>
				</div>

				<!-- Right: what I do with this lesson plan -->
				<div class="flex items-center gap-2" data-testid="footer-plan-actions">
					<ActionConfirm
						v-if="showCreateVariantButton"
						position="top-end"
						class="w-[34rem]"
						v-slot="confirmationDialog"
						:message="$t('Please respect the author’s work when using the lesson plan as a template for a new lesson plan.')"
						:explainer="$t('You can use this lesson plan as a template for your own lesson plan, but you’re not allowed to distribute it further outside your organisation. Also, check in the description of the lesson plan if any additional copyrights apply.')"
						:confirm-label="$t('OK')"
						:cancel-label="$t('Cancel')"
						button-orientation="vertical"
						confirm-style="primary"
					>
						<button type="button" class="btn btn-ghost flex-nowrap" @click="confirmCreateVariant(confirmationDialog)">
							<DocumentDuplicateIcon class="w-4 h-4" />
							<!-- a variant: a new lesson plan based on this one, whoever's it is -->
							<span class="whitespace-nowrap">{{ variantLabel }}</span>
						</button>
					</ActionConfirm>

					<button v-if="withSelectButtons" class="btn btn-primary min-w-28" @click="selectOnly" :disabled="saving || isAlreadyInBlock">
						<span v-if="saving" class="loading loading-spinner"></span>
						{{ $t("Select") }}
					</button>

					<!-- never together with Select: you only edit outside the read-only library of the agenda drawers -->
					<button v-if="withEditButton"
						type="button"
						class="btn btn-primary min-w-28"
						data-testid="edit-button"
						@click="$emit('editLessonPlan', lessonPlan)"
					>
						<PencilIcon class="w-4 h-4" />
						{{ $t("Edit") }}
					</button>
				</div>
			</div>
		</template>
	</Drawer>
	<LessonPlanPrint :lessonPlan="lessonPlan" />
</template>

<script lang="ts">
import Drawer from "@/components/ui/Drawer.v2.vue";
import ActionConfirm from "@/components/ui/ActionConfirm.vue";
import { PencilIcon, ChevronDownIcon, ChevronUpIcon, PrinterIcon, DocumentDuplicateIcon, RectangleStackIcon, PlusIcon, CheckIcon } from "@heroicons/vue/24/outline";
import { PopoverAnchor, PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui';
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { mapStores } from "pinia";
import LessonPlanCollectionsSelector from "@/components/LessonPlanCollectionsSelector.vue";
import GoalsDisplay from "@/components/GoalsDisplay.vue";
import Attachments from "@/components/Attachments.vue";
import { useUsersStore } from "@/stores/Users.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { LessonPlan } from "@/models/LessonPlan.model";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { tiptapUtils } from '@/utils/TipTap.util.ts';
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import TextEditor from '@/components/ui/TextEditor.vue';
import LessonPlanPrint from '@/components/LessonPlanPrint.vue';

export default {
	emits: ["requestUnmount", "selectLessonPlan", "editLessonPlan", "collectionsChanged", "createVariant", "removedFromLibrary"],

	props: {
		lessonPlan: {
			type: Object as () => LessonPlan,
			default: null,
		},
		initialWidth: {
			type: String,
			default: '3xl',
		},
		withSelectButtons: {
			type: Boolean,
			default: false,
		},
		withEditButton: {
			type: Boolean,
			default: false,
		},
		// The variant button's text, from the parent that knows what happens with the variant
		// (e.g. it replaces the plan on a lesson item). Falls back to "Create variant".
		variantButtonLabel: {
			type: String,
			default: '',
		},
		skipOpenAnimation: {
			type: Boolean,
			default: false,
		},
		// The library button in the footer: add to or remove from My Library, and its collections
		withLibraryButton: {
			type: Boolean,
			default: false,
		},
		// Close once the plan is removed from My Library: in the library itself it leaves the list,
		// in the agenda the lesson keeps it, so the viewer stays open there
		closeOnRemovedFromLibrary: {
			type: Boolean,
			default: false,
		},
		collectionTooltipMessage: {
			type: String,
			default: '',
		},
	},

	components: {
		Drawer,
		ActionConfirm,
		PencilIcon,
		ChevronDownIcon,
		ChevronUpIcon,
		PrinterIcon,
		DocumentDuplicateIcon,
		RectangleStackIcon,
		PlusIcon,
		CheckIcon,
		LessonPlanCollectionsSelector,
		GoalsDisplay,
		Attachments,
		PopoverAnchor,
		PopoverArrow,
		PopoverContent,
		PopoverPortal,
		PopoverRoot,
		PopoverTrigger,
		TextEditor,
		LessonPlanPrint
	},

	data() {
		return {
			slotHide: null as any,
			saving: false,
			showAllSupplies: false,
			showAllAttachments: false,
		}
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useLessonPlansStore, useUsersStore, useDiscoverStateStore, useOrganisationsStore),

		// Authorship (who made it), not edit rights: see canBeEditedBy() for those.
		isCreatedByMe(): boolean {
			if(this.lessonPlan && this.usersStore.me) {
				return this.lessonPlan.isCreatedBy(this.usersStore.me);
			}
			return false;
		},

		// Your own plan, still on a lesson after you removed it from your library: no longer editable there.
		isRemovedFromMyLibrary(): boolean {
			const me = this.usersStore.me;
			return !!me && !!this.lessonPlan.id && !this.lessonPlan.isInMyLibrary && this.lessonPlan.isOwnedBy(me);
		},

		// A past lesson can keep an older version of a plan in my library: my library has the latest one.
		isOutdatedVersionInMyLibrary(): boolean {
			return !!this.lessonPlan.isInMyLibrary && this.lessonPlan.isOutdatedVersion();
		},

		outdatedVersionCollections(): LessonPlanCollection[] {
			return (this.lessonPlan.collectionIds ?? [])
				.map((id: string) => this.lessonPlanCollectionsStore.find(id))
				.filter((collection: LessonPlanCollection | undefined) => !!collection);
		},

		variantLabel(): string {
			return this.variantButtonLabel || this.$t("Create variant");
		},

		// No variant of a draft you can edit (not in your library): you edit that one instead.
		showCreateVariantButton(): boolean {
			const canEdit = !!this.usersStore.me && this.lessonPlan.canBeEditedBy(this.usersStore.me);
			return !canEdit || this.lessonPlan.isInMyLibrary;
		},

		showFooter(): boolean {
			return this.withLibraryButton
				|| this.showCreateVariantButton
				|| this.withSelectButtons
				|| this.withEditButton;
		},

		visibleAttachments() {
			if (!this.lessonPlan?.attachments?.length) return [];
			const sorted = [...this.lessonPlan.attachments].sort((a: any, b: any) =>
				b.created_at.localeCompare(a.created_at)
			);
			return this.showAllAttachments ? sorted : sorted.slice(0, 3);
		},

		isAlreadyInBlock(): boolean {
			if (!this.lessonPlan?.id) return false;
			return this.discoverStateStore.lessonPlanIdsInBlock.includes(this.lessonPlan.id);
		},

		// similar function exists on LessonActivities component - keep them in sync
		showCreator(): boolean {
			// show creator info if it's not your own lesson plan...
			if (!this.isCreatedByMe) {
				return true;
			// ...or if it's your own but you are not the original creator
			} else if( this.lessonPlan.originalCreator?.id && this.lessonPlan.originalCreator.id !== this.usersStore.me?.id) {
				return true;
			}
			return false;
		}
	},

	methods: {
		instructionsToHtml(instructions: string) {
			if(!instructions) return '';
			instructions = instructions.trim();
			// Check if the string looks like a valid json object (meaning it's tiptap generated content)
			// If it fails, meaning it's most likely a string from an older lesson plan, return the original string (after escaping it to prevent XSS)
			try {
				const instructionsAsObject = JSON.parse(instructions);
				return tiptapUtils.jsonToHtml(instructionsAsObject);
			} catch (e) {
				const div = document.createElement('div');
				div.textContent = instructions;
				return div.innerHTML.replace(/\n/g, '<br>'); // preserve line breaks
			}
		},

		setSlotHide(hideFn: any) {
			if (!this.slotHide) this.slotHide = hideFn;
			return;
		},

		hide() {
			this.$emit("requestUnmount");
		},

		animatedClose() {
			this.slotHide ? this.slotHide() : this.hide();
		},

		async onOutdatedPopoverOpen(open: boolean) {
			// the collection names: in the agenda, the collections may not be loaded yet
			if (open) await this.lessonPlanCollectionsStore.loadOnce();
		},

		onRemovedFromLibrary(lessonPlan: LessonPlan) {
			this.$emit("removedFromLibrary", lessonPlan);
			if (this.closeOnRemovedFromLibrary) this.animatedClose();
		},

		printLessonplan() {
			window.print();
		},

		async confirmCreateVariant(confirmationDialog: any) {
			// If it's not your own lesson plan, first show a confirmation dialog with a copyright reminder
			if (!this.isCreatedByMe) {
				const confirmed = await confirmationDialog.show();
				if (!confirmed) return;
			}

			this.$emit("createVariant", this.lessonPlan);
			this.animatedClose();
		},

		selectOnly() {
			this.$emit("selectLessonPlan", this.lessonPlan);
			this.animatedClose();
		},
	},
};
</script>
